import React from 'react';
import { Space, Table } from 'antd';

type Props = {
  targets: any[];
  loading?: boolean;
  page: number;
  pageSize: number;
  onPageChange: (page: number, pageSize: number) => void;
  inventoryColumns: any[];
  historyIocColumns: any[];
  historyIocRows?: any[];
  historyIocLoading?: boolean;
  headerAction?: React.ReactNode;
  historyTitleText?: string;
};

const PAGE_SIZE_OPTIONS = ['10', '20', '50', '100'];

const badgeStyle = {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 4,
  fontWeight: 600 as const,
};

const InstructionTargetsOverview: React.FC<Props> = ({
  targets,
  loading = false,
  page,
  pageSize,
  onPageChange,
  inventoryColumns,
  historyIocColumns,
  historyIocRows = [],
  historyIocLoading = false,
  headerAction,
  historyTitleText = '操作对象（历史指令产出结果）',
}) => {
  const list = Array.isArray(targets) ? targets : [];
  const hasHistoryIoc = (list || []).some((r: any) => String(r?.target_type || '').trim() === 'instruction_output');

  if (hasHistoryIoc) {
    const rows = Array.isArray(historyIocRows) ? historyIocRows : [];
    const parseIocCodeParts = (codeRaw: any) => {
      const code = String(codeRaw || '').trim();
      const parts = code.split('-');
      const n = parts.length;
      const mid = n >= 2 ? Number(parts[n - 2]) : NaN;
      const tail = n >= 1 ? Number(parts[n - 1]) : NaN;
      return {
        code,
        mid: Number.isFinite(mid) ? mid : Number.MAX_SAFE_INTEGER,
        tail: Number.isFinite(tail) ? tail : Number.MAX_SAFE_INTEGER,
        midKey: n >= 2 ? String(parts[n - 2]) : '',
      };
    };
    const projectKey = (r: any) => {
      const pid = Number(r?.project_id);
      const pcode = String(r?.project_code || '').trim();
      const pname = String(r?.project_name || '').trim();
      return `${Number.isFinite(pid) ? pid : ''}|${pcode}|${pname}`;
    };
    const codeOf = (r: any) => String(r?.output_code || r?.display_code || '').trim();
    const parentByCode = new Map<string, string>();
    const childByParent = new Map<string, Set<string>>();
    (rows || []).forEach((r: any) => {
      const code = codeOf(r);
      if (!code) return;
      const parentCode = String(r?.parent_code || '').trim();
      if (parentCode) parentByCode.set(code, parentCode);
      const childCodes = Array.isArray(r?.child_codes) ? r.child_codes : [];
      childCodes.map((x: any) => String(x || '').trim()).filter(Boolean).forEach((cc: string) => {
        const s = childByParent.get(code) || new Set<string>();
        s.add(cc);
        childByParent.set(code, s);
      });
    });
    // 兜底：如果只有 parent_code，反向补出 childByParent 关系
    parentByCode.forEach((p, c) => {
      const s = childByParent.get(p) || new Set<string>();
      s.add(c);
      childByParent.set(p, s);
    });
    const getHierarchyRole = (r: any): 'parent' | 'child' | 'standalone' => {
      const code = codeOf(r);
      if (!code) return 'standalone';
      const parentCode = String(parentByCode.get(code) || r?.parent_code || '').trim();
      if (parentCode) return 'child';
      const hasChildren = (childByParent.get(code)?.size || 0) > 0
        || !!r?.is_parent_container
        || (Array.isArray(r?.child_codes) && r.child_codes.length > 0);
      if (hasChildren) return 'parent';
      return 'standalone';
    };
    const getRootCode = (r: any) => {
      const code = codeOf(r);
      const parentCode = String(parentByCode.get(code) || r?.parent_code || '').trim();
      return parentCode || code;
    };
    const sortedRows = [...rows].sort((a: any, b: any) => {
      const apc = String(a?.project_code || '').trim();
      const bpc = String(b?.project_code || '').trim();
      if (apc !== bpc) return apc.localeCompare(bpc, 'zh-Hans-CN');
      const apn = String(a?.project_name || '').trim();
      const bpn = String(b?.project_name || '').trim();
      if (apn !== bpn) return apn.localeCompare(bpn, 'zh-Hans-CN');
      const aid = Number(a?.project_id || 0);
      const bid = Number(b?.project_id || 0);
      if (aid !== bid) return aid - bid;
      const roleA = getHierarchyRole(a);
      const roleB = getHierarchyRole(b);
      const rootA = getRootCode(a);
      const rootB = getRootCode(b);
      const rootMetaA = parseIocCodeParts(rootA);
      const rootMetaB = parseIocCodeParts(rootB);
      if (rootMetaA.mid !== rootMetaB.mid) return rootMetaA.mid - rootMetaB.mid;
      if (rootMetaA.tail !== rootMetaB.tail) return rootMetaA.tail - rootMetaB.tail;
      const groupKeyA = roleA === 'standalone' ? `self::${rootA}` : `hier::${rootA}`;
      const groupKeyB = roleB === 'standalone' ? `self::${rootB}` : `hier::${rootB}`;
      if (groupKeyA !== groupKeyB) return groupKeyA.localeCompare(groupKeyB);
      const roleOrderA = roleA === 'parent' ? 0 : (roleA === 'child' ? 1 : 0);
      const roleOrderB = roleB === 'parent' ? 0 : (roleB === 'child' ? 1 : 0);
      if (roleOrderA !== roleOrderB) return roleOrderA - roleOrderB;
      const ac = parseIocCodeParts(a?.output_code || a?.display_code);
      const bc = parseIocCodeParts(b?.output_code || b?.display_code);
      if (ac.tail !== bc.tail) return ac.tail - bc.tail;
      return ac.code.localeCompare(bc.code);
    });
    const rowBgMap = new Map<string, string>();
    const rowProjectHeadMap = new Map<string, boolean>();
    const rowGroupBorderMap = new Map<string, { grouped: boolean; top: boolean; bottom: boolean }>();
    const rowGroupKeyMap = new Map<string, string>();
    const groupCount = new Map<string, number>();
    let stripe = -1;
    let lastProject = '';
    let lastBand = '';
    sortedRows.forEach((r: any) => {
      const rk = String(r?.__key || r?.output_container_id || r?.output_code || '');
      const pk = projectKey(r);
      const role = getHierarchyRole(r);
      const rootCode = getRootCode(r);
      const groupKey = role === 'standalone' ? `${pk}|standalone:${rootCode}` : `${pk}|hier:${rootCode}`;
      rowGroupKeyMap.set(rk, groupKey);
      groupCount.set(groupKey, (groupCount.get(groupKey) || 0) + 1);
      const codeInfo = parseIocCodeParts(rootCode);
      const band = `${pk}|${codeInfo.midKey}|${groupKey}`;
      const isProjectHead = pk !== lastProject;
      if (isProjectHead) rowProjectHeadMap.set(rk, true);
      if (band !== lastBand) {
        stripe += 1;
        lastBand = band;
      }
      rowBgMap.set(rk, stripe % 2 === 0 ? '#e6f7ff' : '#fff7e6');
      lastProject = pk;
    });
    sortedRows.forEach((r: any, idx: number) => {
      const rk = String(r?.__key || r?.output_container_id || r?.output_code || '');
      const gk = String(rowGroupKeyMap.get(rk) || '');
      const prevRk = idx > 0 ? String(sortedRows[idx - 1]?.__key || sortedRows[idx - 1]?.output_container_id || sortedRows[idx - 1]?.output_code || '') : '';
      const nextRk = idx < sortedRows.length - 1 ? String(sortedRows[idx + 1]?.__key || sortedRows[idx + 1]?.output_container_id || sortedRows[idx + 1]?.output_code || '') : '';
      const prevGk = prevRk ? String(rowGroupKeyMap.get(prevRk) || '') : '';
      const nextGk = nextRk ? String(rowGroupKeyMap.get(nextRk) || '') : '';
      const grouped = gk.includes('|hier:') && Number(groupCount.get(gk) || 0) > 1;
      rowGroupBorderMap.set(rk, {
        grouped,
        top: grouped && prevGk !== gk,
        bottom: grouped && nextGk !== gk,
      });
    });
    const total = sortedRows.length;
    const prjCount = (() => {
      try {
        const set = new Set<string>();
        sortedRows.forEach((r: any) => {
          const pid = Number(r?.project_id);
          if (Number.isFinite(pid)) set.add(String(pid));
          else if (r?.project_code) set.add(`code:${String(r.project_code)}`);
        });
        return set.size;
      } catch {
        return 0;
      }
    })();
    const totalQty = sortedRows.reduce((acc: number, r: any) => {
      const n = Number(
        r?.output_contents_direct_total_qty != null
          ? r?.output_contents_direct_total_qty
          : (r?.output_contents_total_qty || 0),
      );
      return acc + (Number.isFinite(n) ? n : 0);
    }, 0);
    const hierarchyColumn = {
      title: '层级标识',
      key: '__hierarchy_role__',
      width: 120,
      render: (_: any, r: any) => {
        const role = getHierarchyRole(r);
        const roleText = role === 'parent' ? '父容器' : (role === 'child' ? '子容器' : '独立容器');
        const roleStyle = role === 'parent'
          ? { color: '#1677ff', border: '#91caff', bg: '#e6f4ff' }
          : (role === 'child'
            ? { color: '#d4380d', border: '#ffbb96', bg: '#fff2e8' }
            : { color: '#389e0d', border: '#b7eb8f', bg: '#f6ffed' });
        return (
          <span
            style={{
              display: 'inline-block',
              minWidth: 76,
              textAlign: 'center',
              padding: '2px 10px',
              borderRadius: 6,
              border: `1px solid ${roleStyle.border}`,
              color: roleStyle.color,
              background: roleStyle.bg,
              lineHeight: 1.4,
            }}
          >
            {roleText}
          </span>
        );
      },
    } as any;
    const hasHierarchyCol = (Array.isArray(historyIocColumns) ? historyIocColumns : []).some((c: any) => String(c?.key || c?.dataIndex || '').trim() === '__hierarchy_role__');
    const baseCols = Array.isArray(historyIocColumns) ? historyIocColumns : [];
    const outputCodeIdx = baseCols.findIndex((c: any) => String(c?.key || c?.dataIndex || '').trim() === 'output_code');
    const finalCols = hasHierarchyCol
      ? baseCols
      : (outputCodeIdx >= 0
        ? [...baseCols.slice(0, outputCodeIdx + 1), hierarchyColumn, ...baseCols.slice(outputCodeIdx + 1)]
        : [hierarchyColumn, ...baseCols]);
    return (
      <>
        <style>{`
          .instruction-targets-history-ioc-table .ant-table-tbody > tr.history-ioc-group-row > td {
            border-left: 2px solid #91caff !important;
            border-right: 2px solid #91caff !important;
          }
          .instruction-targets-history-ioc-table .ant-table-tbody > tr.history-ioc-group-start > td {
            border-top: 2px solid #91caff !important;
          }
          .instruction-targets-history-ioc-table .ant-table-tbody > tr.history-ioc-group-end > td {
            border-bottom: 2px solid #91caff !important;
          }
        `}</style>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '8px 0' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <div style={{ fontWeight: 600 }}>{historyTitleText}</div>
            {headerAction}
          </div>
          <Space size={8}>
            <span style={{ ...badgeStyle, border: '1px solid #91caff', background: '#e6f4ff', color: '#0958d9' }}>
              涉及项目数量 {prjCount}
            </span>
            <span style={{ ...badgeStyle, border: '1px solid #ffe58f', background: '#fffbe6', color: '#ad8b00' }}>
              指令产出容器(IOC)总数量 {total}
            </span>
            <span style={{ ...badgeStyle, border: '1px solid #b7eb8f', background: '#f6ffed', color: '#389e0d' }}>
              容器内库存单元总数量 {totalQty}
            </span>
          </Space>
        </div>
        <Table
          className="instruction-targets-history-ioc-table"
          size="small"
          rowKey={(r: any, index?: number) => String(r?.__key || r?.output_container_id || r?.output_code || index)}
          dataSource={sortedRows}
          columns={finalCols as any}
          onRow={(r: any) => {
            const rk = String(r?.__key || r?.output_container_id || r?.output_code || '');
            const isHead = !!rowProjectHeadMap.get(rk);
            const borderMeta = rowGroupBorderMap.get(rk) || { grouped: false, top: false, bottom: false };
            return {
              style: {
                backgroundColor: rowBgMap.get(rk) || '#ffffff',
                borderTop: borderMeta.top ? '2px solid #91caff' : (isHead ? '2px solid #d9d9d9' : undefined),
                borderLeft: borderMeta.grouped ? '2px solid #91caff' : undefined,
                borderRight: borderMeta.grouped ? '2px solid #91caff' : undefined,
                borderBottom: borderMeta.bottom ? '2px solid #91caff' : undefined,
              },
            };
          }}
          rowClassName={(r: any) => {
            const rk = String(r?.__key || r?.output_container_id || r?.output_code || '');
            const borderMeta = rowGroupBorderMap.get(rk) || { grouped: false, top: false, bottom: false };
            if (!borderMeta.grouped) return '';
            if (borderMeta.top) return 'history-ioc-group-row history-ioc-group-start';
            if (borderMeta.bottom) return 'history-ioc-group-row history-ioc-group-end';
            return 'history-ioc-group-row';
          }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: PAGE_SIZE_OPTIONS,
            showTotal: (t: number, range: [number, number]) => `第${range[0]}-${range[1]}条 共${t}条记录`,
            onChange: (p: number, ps: number) => onPageChange(p, ps),
          }}
          loading={loading || historyIocLoading}
        />
      </>
    );
  }

  const total = list.length;
  const totalQty = list.reduce((acc: number, t: any) => {
    const v = Number(t?.expected_qty ?? 0);
    return acc + (Number.isFinite(v) ? v : 0);
  }, 0);
  const prjCount = (() => {
    try {
      const set = new Set<string>();
      (list || []).forEach((r: any) => {
        const pid = r?.project_id ?? r?.target?.project_id;
        if (pid != null) {
          set.add(String(pid));
          return;
        }
        const pcode = r?.project_code || r?.target?.snapshot?.project_code;
        if (pcode) set.add(`code:${String(pcode)}`);
      });
      return set.size;
    } catch {
      return 0;
    }
  })();
  const allKinds = (list || []).filter((r: any) => !!r?.use_all_available).length;
  const partKinds = Math.max(0, total - allKinds);
  const ordered = [...(list.filter((r: any) => !r?.use_all_available)), ...(list.filter((r: any) => !!r?.use_all_available))];
  const start = (page - 1) * pageSize;
  const end = start + pageSize;
  const pageData = ordered.slice(start, end);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '8px 0' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <div style={{ fontWeight: 600 }}>操作对象(当前可用库存)</div>
          {headerAction}
        </div>
        <Space size={8}>
          <span style={{ ...badgeStyle, border: '1px solid #91caff', background: '#e6f4ff', color: '#0958d9' }}>
            涉及项目数量 {prjCount}
          </span>
          <span style={{ ...badgeStyle, border: '1px solid #ffe58f', background: '#fffbe6', color: '#ad8b00' }}>
            操作对象种类 {total} | 操作全部库存种类 {allKinds} | 指定操作数量种类 {partKinds}
          </span>
          <span style={{ ...badgeStyle, border: '1px solid #b7eb8f', background: '#f6ffed', color: '#389e0d' }}>
            预计操作总数量 {totalQty}
          </span>
        </Space>
      </div>
      <Table
        size="small"
        rowKey={(r: any, index?: number) => String(r?.id ?? index)}
        dataSource={pageData}
        columns={inventoryColumns as any}
        onRow={(r: any) => {
          const isAll = !!r?.use_all_available;
          return { style: { backgroundColor: isAll ? '#f6ffed' : '#fff0f6' } };
        }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showQuickJumper: true,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
          showTotal: (t: number, range: [number, number]) => `第${range[0]}-${range[1]}条 共${t}条记录`,
          onChange: (p: number, ps: number) => onPageChange(p, ps),
        }}
        loading={loading}
      />
    </>
  );
};

export default InstructionTargetsOverview;
